import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import { Plus, Search, RefreshCw, ChevronRight, Loader2, Pencil, Home, MapPin, Upload } from "lucide-react";
import { FormDialog, Field, PhotoUploader } from "./AssetForms";
import CsvImportDialog from "./CsvImportDialog";

const STATUS_OPTIONS = ["", "ACTIVE", "INACTIVE"];

export default function Plots() {
  const [rows, setRows] = useState([]);
  const [parks, setParks] = useState([]);
  const [search, setSearch] = useState("");
  const [parkFilter, setParkFilter] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      if (parkFilter) params.park_id = parkFilter;
      if (status) params.status = status;
      const { data } = await adminApi.get("/assets/plots", { params });
      setRows(data.items || []);
    } finally { setLoading(false); }
  }, [search, parkFilter, status]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Park list for the filter dropdown + the Add Plot form.
    adminApi.get("/assets/parks", { params: { limit: 500 } }).then(({ data }) => setParks(data.items || []));
  }, []);

  return (
    <div data-testid="assets-plots-page">
      <div className="admin-page-header flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow">Assets · Plots Register</p>
          <h1>Plots</h1>
          <p className="lead">Every plot we know about. Click to open the plot record and raise a new job.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setImporting(true)} className="text-sm h-10 px-4 rounded-md border border-[#E2DCD0] text-[#071A33] hover:border-[#0F766E] hover:text-[#0F766E] inline-flex items-center gap-1.5" data-testid="plots-import-btn">
            <Upload size={14}/> Bulk import CSV
          </button>
          <button onClick={() => setEditing({})} className="text-sm h-10 px-4 rounded-md bg-[#064E2E] text-white hover:bg-[#0a5b55] inline-flex items-center gap-1.5" data-testid="plots-add-btn">
            <Plus size={14}/> Add Plot
          </button>
          <button onClick={load} className="btn-outline h-10 py-0 px-3" data-testid="plots-refresh"><RefreshCw size={14} className={loading ? "animate-spin" : ""}/></button>
        </div>
      </div>

      <div className="admin-panel p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search size={14} className="text-[#475569]"/>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search plot, owner, manufacturer…" className="field-input m-0 h-9 text-sm flex-1" data-testid="plots-search"/>
        </div>
        <select value={parkFilter} onChange={(e) => setParkFilter(e.target.value)} className="field-input h-9 text-sm m-0" data-testid="plots-park-filter">
          <option value="">All parks</option>
          {parks.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="field-input h-9 text-sm m-0" data-testid="plots-status-filter">
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
      </div>

      <div className="admin-panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] tracking-[0.18em] uppercase text-[#475569] bg-[#F7F4ED]/60">
            <tr className="text-left">
              <th className="px-4 py-3 w-[60px]">Photo</th>
              <th className="px-4 py-3">Plot</th>
              <th className="px-4 py-3">Park</th>
              <th className="px-4 py-3">Owner</th>
              <th className="px-4 py-3">Manufacturer</th>
              <th className="px-4 py-3 text-center">Year</th>
              <th className="px-4 py-3 text-right">Open jobs</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2DCD0]">
            {rows.length === 0 && !loading && <tr><td colSpan={9} className="px-4 py-10 text-center text-[#475569] italic">No plots yet.</td></tr>}
            {rows.map((p) => (
              <tr key={p.id} className="hover:bg-[#F7F4ED]/50 cursor-pointer" data-testid={`plots-row-${p.id}`} onClick={() => window.location.assign(`/adminpanel/assets/plots/${p.id}`)}>
                <td className="px-4 py-3"><PlotThumb src={p.photo_url}/></td>
                <td className="px-4 py-3 font-medium text-[#071A33]">Plot {p.plot_number}</td>
                <td className="px-4 py-3 text-[#475569]">{p.park_name || "—"}</td>
                <td className="px-4 py-3 text-[#475569]">{p.owner_name || "—"}</td>
                <td className="px-4 py-3 text-[#475569]">{p.manufacturer || "—"}</td>
                <td className="px-4 py-3 text-center text-[#475569]">{p.year || "—"}</td>
                <td className="px-4 py-3 text-right tabular-nums">
                  <span className={p.open_jobs > 0 ? "text-[#B88A2A] font-semibold" : "text-[#475569]"}>{p.open_jobs}</span>
                </td>
                <td className="px-4 py-3"><StatusPill status={p.status}/></td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => setEditing(p)} className="text-[12px] text-[#475569] hover:text-[#064E2E] mr-2" data-testid={`plots-edit-${p.id}`}><Pencil size={13}/></button>
                  <Link to={`/adminpanel/assets/plots/${p.id}`} className="text-[12px] px-2 py-1 bg-[#0F766E] text-white hover:bg-[#0a5b55] rounded inline-flex items-center gap-1" data-testid={`plots-open-${p.id}`}>Open <ChevronRight size={11}/></Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="px-4 py-6 text-center text-[#475569]"><Loader2 className="animate-spin inline" size={14}/></div>}
      </div>

      <PlotFormDialog plot={editing} parks={parks} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>

      <CsvImportDialog
        open={importing}
        onClose={() => setImporting(false)}
        onComplete={() => load()}
        title="Bulk import Plots"
        endpoint="/assets/plots/import"
        templateFilename="plots-template.csv"
        templateHeaders={["park_name", "plot_number", "owner_name", "owner_phone", "owner_email", "manufacturer", "model", "year", "occupied", "status"]}
        templateExampleRow={["Riverside Park", "12A", "John Owner", "07700 900000", "john@example.com", "Omar", "Park Lane", "2018", "true", "ACTIVE"]}
        testid="plots-csv-import"
      />
    </div>
  );
}

function PlotThumb({ src }) {
  return (
    <div className="w-12 h-12 rounded overflow-hidden bg-[#F7F4ED] border border-[#E2DCD0] flex items-center justify-center">
      {src ? <img src={`${process.env.REACT_APP_BACKEND_URL}${src}`} alt="" className="w-full h-full object-cover"/> : <Home size={14} className="text-[#94A3B8]"/>}
    </div>
  );
}

function StatusPill({ status }) {
  const s = (status || "ACTIVE").toUpperCase();
  const tone = s === "ACTIVE" ? "bg-[#064E2E]/10 text-[#064E2E]" : "bg-[#475569]/10 text-[#475569]";
  return <span className={`text-[10px] uppercase tracking-[0.12em] font-semibold px-2 py-1 rounded ${tone}`}>{s}</span>;
}

/* Plot create / edit dialog — exported for reuse on Park Record. */
export function PlotFormDialog({ plot, parks, onClose, onSaved }) {
  const isEdit = plot && plot.id;
  const [draft, setDraft] = useState({});
  const [parkList, setParkList] = useState(parks || []);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!plot) return;
    setDraft({
      park_id: plot.park_id || "",
      plot_number: plot.plot_number || "",
      owner_name: plot.owner_name || "",
      owner_phone: plot.owner_phone || "",
      owner_email: plot.owner_email || "",
      manufacturer: plot.manufacturer || "",
      model: plot.model || "",
      year: plot.year || "",
      occupied: plot.occupied !== false,
      photo_upload_id: plot.photo_upload_id || "",
      status: plot.status || "ACTIVE",
    });
    if (!parks?.length) {
      adminApi.get("/assets/parks", { params: { limit: 500 } }).then(({ data }) => setParkList(data.items || []));
    }
  }, [plot, parks]);

  const save = async () => {
    if (!draft.park_id) { toast.error("Park is required"); return; }
    if (!draft.plot_number?.trim()) { toast.error("Plot number is required"); return; }
    setBusy(true);
    try {
      const payload = { ...draft };
      if (payload.year) payload.year = Number(payload.year); else delete payload.year;
      if (isEdit) await adminApi.patch(`/assets/plots/${plot.id}`, payload);
      else await adminApi.post("/assets/plots", payload);
      toast.success(isEdit ? "Plot updated" : "Plot created");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <FormDialog open={!!plot} onClose={onClose} title={isEdit ? `Edit · Plot ${plot?.plot_number || ""}` : "Add Plot"} onSubmit={save} busy={busy} submitLabel={isEdit ? "Save changes" : "Create plot"} testid="plot-form-dialog">
      <PhotoUploader value={draft.photo_upload_id} onChange={(v) => setDraft({ ...draft, photo_upload_id: v })} label="Plot photo" testid="plot-photo-upload"/>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Park *">
          <select className="field-input m-0 h-10" value={draft.park_id || ""} onChange={(e) => setDraft({ ...draft, park_id: e.target.value })} disabled={isEdit} data-testid="plot-park-select">
            <option value="">— Select —</option>
            {parkList.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Plot number *">
          <input className="field-input m-0 h-10" value={draft.plot_number || ""} onChange={(e) => setDraft({ ...draft, plot_number: e.target.value })} data-testid="plot-number-input"/>
        </Field>
        <Field label="Owner name">
          <input className="field-input m-0 h-10" value={draft.owner_name || ""} onChange={(e) => setDraft({ ...draft, owner_name: e.target.value })} data-testid="plot-owner-name"/>
        </Field>
        <Field label="Owner phone">
          <input className="field-input m-0 h-10" value={draft.owner_phone || ""} onChange={(e) => setDraft({ ...draft, owner_phone: e.target.value })}/>
        </Field>
        <Field label="Owner email" full>
          <input type="email" className="field-input m-0 h-10" value={draft.owner_email || ""} onChange={(e) => setDraft({ ...draft, owner_email: e.target.value })} data-testid="plot-owner-email"/>
        </Field>
        <Field label="Manufacturer">
          <input className="field-input m-0 h-10" value={draft.manufacturer || ""} onChange={(e) => setDraft({ ...draft, manufacturer: e.target.value })}/>
        </Field>
        <Field label="Model">
          <input className="field-input m-0 h-10" value={draft.model || ""} onChange={(e) => setDraft({ ...draft, model: e.target.value })}/>
        </Field>
        <Field label="Year">
          <input type="number" min="1900" max="2100" className="field-input m-0 h-10" value={draft.year || ""} onChange={(e) => setDraft({ ...draft, year: e.target.value })}/>
        </Field>
        <Field label="Status">
          <select className="field-input m-0 h-10" value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </Field>
        <Field label="Occupied" full>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={draft.occupied !== false} onChange={(e) => setDraft({ ...draft, occupied: e.target.checked })}/>
            {draft.occupied !== false ? "Occupied" : "Vacant"}
          </label>
        </Field>
      </div>
    </FormDialog>
  );
}
